import type {
  BookingReservationApplicant,
  BookingReservationPartyLookup,
  BookingReservationRow,
} from '../Types/bookingReservationTable.types';

export interface BookingReservationApplicantDisplay {
  title: string;
  subtitle: string;
}

const isValidPartyId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

const joinNonEmpty = (parts: Array<string | undefined | null>, separator: string): string =>
  parts
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(separator);

const getApplicantName = (reservation: BookingReservationRow): string => {
  const firstName = reservation.applicantFirstName || reservation.applicant?.firstName;
  const lastName = reservation.applicantLastName || reservation.applicant?.lastName;

  return joinNonEmpty([firstName, lastName], ' ');
};

const getApplicantAddress = (applicant: BookingReservationApplicant | undefined): string => {
  if (!applicant) {
    return '';
  }

  return joinNonEmpty(
    [applicant.street, joinNonEmpty([applicant.zipCode, applicant.city], ' ')],
    ', '
  );
};

export const getBookingReservationDisplayPartyId = (
  reservation: BookingReservationRow
): number | undefined => {
  const applicantPartyId = reservation.applicantPartyId;
  const invoiceBuyerPartyId = reservation.invoiceBuyerPartyId;
  const hasApplicantParty = isValidPartyId(applicantPartyId);
  const hasInvoiceBuyerParty = isValidPartyId(invoiceBuyerPartyId);

  if (
    hasInvoiceBuyerParty &&
    (!hasApplicantParty || invoiceBuyerPartyId !== applicantPartyId)
  ) {
    return invoiceBuyerPartyId;
  }

  if (hasApplicantParty) {
    return applicantPartyId;
  }

  if (hasInvoiceBuyerParty) {
    return invoiceBuyerPartyId;
  }

  return undefined;
};

export const getBookingReservationApplicantDisplay = (
  reservation: BookingReservationRow,
  parties: BookingReservationPartyLookup[] = []
): BookingReservationApplicantDisplay => {
  const displayPartyId = getBookingReservationDisplayPartyId(reservation);
  const party = parties.find((item) => item.id === displayPartyId);
  const fallbackTitle = getApplicantName(reservation) || '-';
  const fallbackSubtitle = getApplicantAddress(reservation.applicant);

  if (!party) {
    return {
      title: fallbackTitle,
      subtitle: fallbackSubtitle,
    };
  }

  return {
    title: party.label || fallbackTitle,
    subtitle: party.addressSummary || fallbackSubtitle,
  };
};
